import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, X, Loader2, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface PromoCodeInputProps {
  onPromoCodeChange: (code: string) => void;
  appliedCode?: string;
  isLoading?: boolean;
}

export const PromoCodeInput = ({ onPromoCodeChange, appliedCode, isLoading }: PromoCodeInputProps) => {
  const [code, setCode] = useState(appliedCode || "");
  const [validationState, setValidationState] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [discountInfo, setDiscountInfo] = useState<any>(null);
  const [showInput, setShowInput] = useState(!!appliedCode);
  const { toast } = useToast();

  const validatePromoCode = async () => {
    if (!code.trim()) {
      setValidationState('idle');
      setDiscountInfo(null);
      onPromoCodeChange("");
      return;
    }

    setValidationState('validating');
    
    try {
      const { data, error } = await supabase.functions.invoke('validate-promo-code', {
        body: { promoCode: code.trim() }
      });

      if (error) throw error;

      if (data.valid) {
        setValidationState('valid');
        setDiscountInfo(data.discount);
        onPromoCodeChange(code.trim());
        toast({
          title: "Promo code applied!",
          description: data.discount.percent_off 
            ? `${data.discount.percent_off}% discount applied`
            : `$${(data.discount.amount_off / 100).toFixed(2)} discount applied`,
        });
      } else {
        setValidationState('invalid');
        setDiscountInfo(null);
        onPromoCodeChange("");
        toast({
          title: "Invalid promo code",
          description: data.error || "This promo code is not valid or has expired.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Error validating promo code:', error);
      setValidationState('invalid');
      setDiscountInfo(null);
      onPromoCodeChange("");
      toast({
        title: "Validation failed",
        description: "Could not validate promo code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const clearCode = () => {
    setCode("");
    setValidationState('idle');
    setDiscountInfo(null);
    onPromoCodeChange("");
    setShowInput(false);
  };

  if (appliedCode && validationState === 'valid') {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
        <Tag className="h-4 w-4 text-green-600" />
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          {appliedCode}
        </Badge>
        <span className="text-sm text-green-700">Promo code applied!</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearCode}
          className="ml-auto h-6 w-6 p-0 text-green-600 hover:text-green-800"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (!showInput) {
    return (
      <Button
        variant="outline"
        onClick={() => setShowInput(true)}
        className="w-full"
        disabled={isLoading}
      >
        <Tag className="h-4 w-4 mr-2" />
        Have a promo code?
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            placeholder="Enter promo code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && validatePromoCode()}
            disabled={isLoading || validationState === 'validating'}
            className={`pr-10 ${
              validationState === 'valid' ? 'border-green-500' : 
              validationState === 'invalid' ? 'border-red-500' : ''
            }`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {validationState === 'validating' && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
            {validationState === 'valid' && (
              <Check className="h-4 w-4 text-green-500" />
            )}
            {validationState === 'invalid' && (
              <X className="h-4 w-4 text-red-500" />
            )}
          </div>
        </div>
        
        {validationState === 'valid' ? (
          <Button
            variant="outline"
            onClick={clearCode}
            disabled={isLoading}
          >
            Remove
          </Button>
        ) : (
          <Button
            onClick={validatePromoCode}
            disabled={!code.trim() || isLoading || validationState === 'validating'}
          >
            {validationState === 'validating' ? 'Checking...' : 'Apply'}
          </Button>
        )}
      </div>

      {validationState === 'valid' && discountInfo && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            <Check className="h-3 w-3 mr-1" />
            {discountInfo.percent_off 
              ? `${discountInfo.percent_off}% OFF` 
              : `$${(discountInfo.amount_off / 100).toFixed(2)} OFF`}
          </Badge>
          <span className="text-sm text-gray-600">Promo code applied successfully</span>
        </div>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setShowInput(false);
          setCode("");
          setValidationState('idle');
        }}
        className="w-full text-xs"
        disabled={isLoading}
      >
        Cancel
      </Button>
    </div>
  );
};